<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Course Control Central</h1>
        <p class="text-gray-600 mt-1">
          Manage Happy Alien product integrations across all your eLearning
          courses
        </p>
      </div>
      <button
        @click="showAddCourse = true"
        class="btn-primary flex items-center space-x-2"
      >
        <PhPlus class="w-5 h-5" weight="bold" />
        <span>Add Course</span>
      </button>
    </div>

    <!-- Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div class="card">
        <div class="flex items-center">
          <div class="mr-4"><PhBooks class="w-8 h-8 text-primary" weight="fill" /></div>
          <div>
            <p class="text-sm text-gray-600">Total Courses</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ stats.totalCourses }}
            </p>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="flex items-center">
          <div class="mr-4"><PhCheckCircle class="w-8 h-8 text-green-500" weight="fill" /></div>
          <div>
            <p class="text-sm text-gray-600">Active Courses</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ stats.activeCourses }}
            </p>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="flex items-center">
          <div class="mr-4"><PhUsers class="w-8 h-8 text-blue-500" weight="fill" /></div>
          <div>
            <p class="text-sm text-gray-600">Total Learners</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ stats.totalLearners }}
            </p>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="flex items-center">
          <div class="text-3xl text-spark mr-4"><PhSparkle class="inline w-8 h-8 text-yellow-500" weight="fill" /></div>
          <div>
            <p class="text-sm text-gray-600">Sparks Used</p>
            <p class="text-2xl font-bold text-gray-900 font-mono">
              {{ stats.sparksConsumed.toLocaleString() }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Course List -->
    <div class="card">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-900">Your Courses</h2>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="p-12 text-center">
        <div
          class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
        ></div>
        <p class="mt-4 text-gray-600">Loading courses...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="courses.length === 0" class="p-12 text-center">
        <PhBooks class="w-16 h-16 mx-auto mb-4 text-gray-400" weight="fill" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">No courses yet</h3>
        <p class="text-gray-600 mb-6">
          Get started by registering your first eLearning course
        </p>
        <button @click="showAddCourse = true" class="btn-primary">
          Add Your First Course
        </button>
      </div>

      <!-- Course Items -->
      <div v-else class="divide-y divide-gray-200">
        <div
          v-for="course in courses"
          :key="course.course_id"
          class="p-6 hover:bg-gray-50 transition-colors"
        >
          <div class="flex justify-between items-start mb-4">
            <div class="flex-1">
              <h3 class="text-lg font-medium text-gray-900">
                {{ course.title }}
              </h3>
              <div
                class="flex items-center space-x-4 text-sm text-gray-500 mt-1"
              >
                <span>ID: {{ course.course_id }}</span>
                <span v-if="course.platform">
                  <span>•</span>
                  <span>{{ formatPlatform(course.platform) }}</span>
                </span>
                <span v-if="course.last_modified">
                  <span>•</span>
                  <span
                    >Last modified: {{ formatDate(course.last_modified) }}</span
                  >
                </span>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <span class="text-green-600 flex items-center"><PhCheckCircle class="w-4 h-4 mr-1" weight="fill" /> Active</span>
              <button
                @click="viewScript(course)"
                class="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                title="View Script"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                </svg>
              </button>
              <button
                @click="confirmDeleteCourse(course)"
                class="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                title="Delete Course"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Product Scripts for this Course -->
          <div class="bg-gray-50 rounded-lg p-4">
            <h4 class="text-sm font-medium text-gray-900 mb-3">
              Enabled Products
            </h4>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
              <!-- Product Script Cards -->
              <div
                v-for="script in getScriptsForCourse(course.course_id)"
                :key="script.id"
                class="bg-white rounded-lg p-3 border border-gray-200"
              >
                <div class="flex items-center justify-between mb-2">
                  <div>
                    <div class="font-medium text-sm">
                      {{ formatProductName(script.product_name) }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ script.feature_name }}
                    </div>
                  </div>
                  <button
                    @click="
                      toggleScript(
                        course.course_id,
                        script.id,
                        script.status !== 'active'
                      )
                    "
                    :class="[
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                      script.status === 'active' ? 'bg-primary' : 'bg-gray-200',
                    ]"
                  >
                    <span
                      :class="[
                        'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                        script.status === 'active'
                          ? 'translate-x-6'
                          : 'translate-x-1',
                      ]"
                    />
                  </button>
                </div>
                <div class="text-xs text-gray-400">
                  {{ script.spark_cost }} sparks/load
                </div>
              </div>

              <!-- Add New Product Button -->
              <button
                @click="showAddProduct(course.course_id)"
                class="bg-white rounded-lg p-3 border-2 border-dashed border-gray-300 hover:border-primary text-gray-500 hover:text-primary transition-colors flex flex-col items-center"
              >
                <PhPlus class="w-6 h-6 mb-1" weight="bold" />
                <div class="text-xs">Add Product</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Course Modal -->
    <CourseRegistrationModal
      v-if="showAddCourse"
      :open="showAddCourse"
      :org-id="orgId"
      :existing-course="courseToReprocess"
      @close="() => { showAddCourse = false; courseToReprocess = null; }"
      @success="handleCourseAdded"
    />

    <!-- Script View Modal -->
    <ScriptViewModal
      v-if="selectedCourse && showScriptModal"
      :open="showScriptModal"
      :course="selectedCourse"
      :org-id="orgId"
      @close="showScriptModal = false"
      @reprocess="handleReprocess"
    />

    <!-- Add Product Modal -->
    <AddProductModal
      v-if="selectedCourseId && showAddProductModal"
      :open="showAddProductModal"
      :course-id="selectedCourseId"
      @close="showAddProductModal = false"
      @success="handleProductAdded"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Delete Course</h3>
        
        <div v-if="deleteValidationError" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-800 mb-2">{{ deleteValidationError }}</p>
          <div v-if="attachedProducts && attachedProducts.length > 0">
            <p class="text-sm text-red-700 mb-2">Attached products:</p>
            <ul class="list-disc list-inside text-sm text-red-600">
              <li v-for="product in attachedProducts" :key="`${product.product}-${product.feature}`">
                {{ formatProductName(product.product) }} - {{ product.feature }}
              </li>
            </ul>
          </div>
        </div>
        
        <div v-else>
          <p class="text-gray-600 mb-4">
            Are you sure you want to delete <strong>{{ courseToDelete?.title }}</strong>?
          </p>
          <p class="text-sm text-gray-500 mb-4">
            This action cannot be undone. Make sure no products are attached to this course.
          </p>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            v-if="!deleteValidationError"
            @click="deleteCourse"
            :disabled="isDeleting"
            class="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isDeleting ? 'Deleting...' : 'Delete Course' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { PhSparkle, PhPlus, PhBooks, PhCheckCircle, PhUsers } from "@phosphor-icons/vue";
import { useAuthStore } from "../stores/auth";
import { useNotificationStore } from "../stores/notifications";
import CourseRegistrationModal from "../components/CourseRegistrationModal.vue";
import ScriptViewModal from "../components/ScriptViewModal.vue";
import AddProductModal from "../components/AddProductModal.vue";

interface Course {
  course_id: string;
  org_id: string;
  title: string;
  platform?: string;
  last_modified?: string;
  metadata?: any;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  // Legacy fields for compatibility
  id?: string;
  tool_type?: string;
  integration_method?: string;
  active?: boolean;
  last_activity_at?: string;
}

interface CourseScript {
  id: string;
  course_id: string;
  product_name: string;
  feature_name: string;
  status: string;
  spark_cost: number;
}

const authStore = useAuthStore();
const notificationStore = useNotificationStore();

// State
const courses = ref<Course[]>([]);
const courseScripts = ref<Record<string, CourseScript[]>>({});
const isLoading = ref(false);
const showAddCourse = ref(false);
const showScriptModal = ref(false);
const showAddProductModal = ref(false);
const selectedCourse = ref<Course | null>(null);
const selectedCourseId = ref<string | null>(null);
const showDeleteModal = ref(false);
const courseToDelete = ref<Course | null>(null);
const courseToReprocess = ref<Course | null>(null);
const isDeleting = ref(false);
const deleteValidationError = ref<string>('');
const attachedProducts = ref<Array<{product: string; feature: string}>>();

// Computed
const orgId = computed(
  () => authStore.user?.organization_id || authStore.user?.id || "demo-org"
);

const stats = computed(() => ({
  totalCourses: courses.value.length,
  activeCourses: courses.value.filter((c) => c.active).length,
  totalLearners: 0, // TODO: Get from analytics
  sparksConsumed: Object.values(courseScripts.value)
    .flat()
    .reduce((sum, script) => sum + (script.spark_cost || 0), 0),
}));

// Methods
async function loadCourses() {
  isLoading.value = true;
  try {
    // Use local API in development, production API in production
    const apiUrl = import.meta.env.DEV
      ? "http://localhost:3001"
      : "https://api.happyalien.ai";
    const response = await fetch(`${apiUrl}/courses`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${authStore.accessToken}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      courses.value = data.data || [];

      // Load scripts for each course (skip if no ID)
      for (const course of courses.value) {
        if (course.id) {
          await loadScriptsForCourse(course.id);
        }
      }
    } else if (response.status === 401) {
      // Use local storage fallback
      const localCourses = localStorage.getItem("echocast_courses");
      if (localCourses) {
        courses.value = JSON.parse(localCourses);
      }
    }
  } catch (error) {
    console.error("Failed to load courses:", error);
    // Use local storage fallback
    const localCourses = localStorage.getItem("echocast_courses");
    if (localCourses) {
      courses.value = JSON.parse(localCourses);
    }
  } finally {
    isLoading.value = false;
  }
}

async function loadScriptsForCourse(courseId: string) {
  try {
    const apiUrl = import.meta.env.DEV
      ? "http://localhost:3001"
      : "https://api.happyalien.ai";
    const response = await fetch(`${apiUrl}/courses/${courseId}/scripts`, {
      credentials: "include",
      headers: {
        Authorization: `Bearer ${authStore.accessToken}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      courseScripts.value[courseId] = data.data || [];
    }
  } catch (error) {
    console.error(`Failed to load scripts for course ${courseId}:`, error);
    // Default to empty array
    courseScripts.value[courseId] = [];
  }
}

function getScriptsForCourse(courseId: string): CourseScript[] {
  return courseScripts.value[courseId] || [];
}

async function toggleScript(
  courseId: string,
  scriptId: string,
  enable: boolean
) {
  try {
    const apiUrl = import.meta.env.DEV
      ? "http://localhost:3001"
      : "https://api.happyalien.ai";
    const response = await fetch(
      `${apiUrl}/courses/${courseId}/scripts/${scriptId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${authStore.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: enable ? "active" : "paused",
        }),
      }
    );

    if (response.ok) {
      await loadScriptsForCourse(courseId);
      notificationStore.success(
        "Script Updated",
        `Product ${enable ? "enabled" : "disabled"} successfully`
      );
    }
  } catch (error) {
    console.error("Failed to toggle script:", error);
    notificationStore.error(
      "Update Failed",
      "Could not update script status"
    );
  }
}

function showAddProduct(courseId: string) {
  selectedCourseId.value = courseId;
  showAddProductModal.value = true;
}

function handleCourseAdded(data: {
  courseId: string;
  title: string;
  method: string;
}) {
  // Reload courses from API to get the newly added course
  loadCourses();

  notificationStore.success(
    "Course Registered",
    `${data.title} has been added successfully`
  );
}

function handleProductAdded() {
  // Reload scripts for the course
  if (selectedCourseId.value) {
    loadScriptsForCourse(selectedCourseId.value);
  }

  notificationStore.success(
    "Product Added",
    "Product has been enabled for this course"
  );
}

function handleReprocess(course: Course) {
  // Store the course to reprocess and open registration modal with it
  courseToReprocess.value = course;
  showAddCourse.value = true;
  showScriptModal.value = false;
}

function viewScript(course: Course) {
  selectedCourse.value = course;
  showScriptModal.value = true;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 24) {
    if (diffInHours < 1) return "Just now";
    return `${Math.floor(diffInHours)}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
}

function formatPlatform(platform: string): string {
  const platforms: { [key: string]: string } = {
    storyline: "Articulate Storyline",
    rise: "Articulate Rise",
    captivate: "Adobe Captivate",
    lectora: "Lectora",
    scorm: "SCORM Package",
    custom: "Custom/Other",
  };
  return platforms[platform] || platform;
}

function formatToolType(toolType: string): string {
  // Keep for backward compatibility
  return formatPlatform(toolType);
}

function formatIntegrationMethod(method: string): string {
  const methods: { [key: string]: string } = {
    copy_paste: "Manual Script",
    package_upload: "SCORM Upload",
    manual: "Manual",
    upload: "Upload",
  };
  return methods[method] || method;
}

function formatProductName(product: string): string {
  const names: { [key: string]: string } = {
    echocast: "CourseSignal",
    analytics: "Analytics",
    chatbot: "AI Chatbot",
    other: "Other",
  };
  return names[product] || product;
}

function confirmDeleteCourse(course: Course) {
  courseToDelete.value = course;
  deleteValidationError.value = '';
  attachedProducts.value = undefined;
  showDeleteModal.value = true;
}

function cancelDelete() {
  showDeleteModal.value = false;
  courseToDelete.value = null;
  deleteValidationError.value = '';
  attachedProducts.value = undefined;
}

async function deleteCourse() {
  if (!courseToDelete.value) return;
  
  isDeleting.value = true;
  deleteValidationError.value = '';
  
  try {
    const apiUrl = import.meta.env.DEV
      ? "http://localhost:3001"
      : "https://api.happyalien.ai";
    
    const response = await fetch(
      `${apiUrl}/courses/${courseToDelete.value.course_id}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${authStore.accessToken}`,
        },
      }
    );
    
    const data = await response.json();
    
    if (!response.ok) {
      if (response.status === 400 && data.attachedProducts) {
        deleteValidationError.value = data.error;
        attachedProducts.value = data.attachedProducts;
      } else {
        deleteValidationError.value = data.error || "Failed to delete course";
      }
      return;
    }
    
    // Success - reload courses and close modal
    await loadCourses();
    
    notificationStore.success(
      "Course Deleted",
      `${courseToDelete.value.title} has been removed successfully`
    );
    
    cancelDelete();
  } catch (error) {
    console.error("Failed to delete course:", error);
    deleteValidationError.value = "Network error. Please try again.";
  } finally {
    isDeleting.value = false;
  }
}

// Lifecycle
onMounted(() => {
  loadCourses();
});
</script>
